import type { AppNavigationState, AppScreenId } from './appNavigation.js';

export interface AppShellNavigationItemViewModel {
  screenId: AppScreenId;
  label: string;
  enabled: boolean;
  active: boolean;
  reason?: string;
}

export interface AppShellViewModel {
  model: 'APP-SHELL-0.1.0';
  navigationMode: 'desktop_sidebar' | 'mobile_bottom';
  skipLinkTarget: '#main-content';
  minimumTouchTargetPx: 44;
  items: readonly AppShellNavigationItemViewModel[];
  disclosure: string;
}

const LABELS: Readonly<Record<AppScreenId, string>> = Object.freeze({
  birth_input: '出生資料',
  natal_chart: '十二宮命盤',
  natal_interpretation: '詳細解讀',
  natal_timing: '運勢',
  relationship: '雙人分析',
  relationship_result: '雙人結果',
  data_management: '資料管理',
  settings: '設定',
});

function requirement(state: AppNavigationState, screenId: AppScreenId): string | undefined {
  if (['natal_chart', 'natal_interpretation', 'natal_timing'].includes(screenId) && !state.natalResultAvailable) {
    return '需要先完成本命排盤。';
  }
  if (screenId === 'relationship_result' && !state.relationshipResultAvailable) {
    return '需要先完成雙人分析。';
  }
  return undefined;
}

export function buildAppShellViewModel(
  state: AppNavigationState,
  viewport: 'desktop' | 'mobile' = 'desktop',
): AppShellViewModel {
  const order: readonly AppScreenId[] = Object.freeze([
    'birth_input',
    'natal_chart',
    'natal_interpretation',
    'natal_timing',
    'relationship',
    'relationship_result',
    'data_management',
    'settings',
  ]);
  const items = order.map((screenId) => {
    const reason = requirement(state, screenId);
    return Object.freeze({
      screenId,
      label: LABELS[screenId],
      enabled: reason === undefined,
      active: state.activeScreen === screenId,
      ...(reason ? { reason } : {}),
    });
  });
  return Object.freeze({
    model: 'APP-SHELL-0.1.0' as const,
    navigationMode: viewport === 'mobile' ? 'mobile_bottom' as const : 'desktop_sidebar' as const,
    skipLinkTarget: '#main-content' as const,
    minimumTouchTargetPx: 44 as const,
    items: Object.freeze(items),
    disclosure: '主導航只依賴安全的 AppNavigationState；沒有本命或雙人結果時相關畫面會被停用。手機與桌面共用同一可達性規則。',
  });
}
